import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { chartBlockToMarkdown, chartSourcePath, chartSpecForEditor, chartTitle, cleanChartSpecForSave, parseMarkdownBlocks } from "./GuttenbergChartMarkdown";
import { parseMermaidFlowchart } from "./GuttenbergMermaid";

function chartPreviewBlockFromMarkdown(markdown) {
    return parseMarkdownBlocks(markdown ?? "").find((block) => block.type === "chart") ?? null;
}

function tablePreviewBlocksFromMarkdown(markdown) {
    return parseMarkdownBlocks(markdown ?? "").filter((block) => block.type === "table");
}

function chartCommentTitle(markdown) {
    const block = chartPreviewBlockFromMarkdown(markdown);
    return block ? chartTitle(block.spec) : "graphe";
}

function chartSpecDiffItems(beforeSpec, afterSpec) {
    const fields = [
        ["Type", (spec) => spec?.chart_type ?? spec?.type],
        ["X min", (spec) => spec?.x_min ?? spec?.axes?.x?.min],
        ["X max", (spec) => spec?.x_max ?? spec?.axes?.x?.max],
        ["Y min", (spec) => spec?.y_min ?? spec?.axes?.y?.min],
        ["Y max", (spec) => spec?.y_max ?? spec?.axes?.y?.max],
        ["Points", (spec) => spec?.points ?? spec?.display?.points],
        ["Largeur ligne", (spec) => spec?.line_width ?? spec?.lineWidth ?? spec?.display?.lineWidth],
        ["Legende", (spec) => spec?.legend ?? spec?.display?.legend],
        ["Grille", (spec) => spec?.grid ?? spec?.display?.grid],
    ];
    return fields
        .map(([label, getter]) => {
        const beforeValue = formatChartDiffValue(getter(beforeSpec));
        const afterValue = formatChartDiffValue(getter(afterSpec));
        return beforeValue === afterValue ? null : { label, before: beforeValue, after: afterValue };
    })
        .filter(Boolean);
}

function formatChartDiffValue(value) {
    if (value === undefined || value === null || value === "")
        return "";
    if (typeof value === "boolean")
        return value ? "oui" : "non";
    return String(value);
}

function ImageDiffColumn({ title, images, projectId, emptyLabel }) {
    return (<div className="suggestion-image-column">
      <strong>{title}</strong>
      {images.length ? (images.map((image) => (<figure key={`${title}-${image.path}`}>
            <img src={reportImageSrc(projectId, image.path)} alt={image.alt || image.caption || title}/>
            <figcaption>{image.caption || image.alt || image.path}</figcaption>
          </figure>))) : (<span>{emptyLabel}</span>)}
    </div>);
}

function markdownImageRefs(markdown) {
    const lines = (markdown ?? "").split(/\r?\n/);
    const refs = [];
    const seen = new Set();
    lines.forEach((line, index) => {
        const match = line.match(/!\[([^\]]*)\]\(([^)]+)\)/);
        if (!match)
            return;
        const path = match[2].trim();
        if (!path || seen.has(path))
            return;
        seen.add(path);
        const captionMatch = lines[index + 1]?.trim().match(/^\*([^*]+)\*$/);
        refs.push({
            path,
            alt: match[1].trim(),
            caption: captionMatch?.[1]?.trim() ?? ""
        });
    });
    return refs;
}

function reportImageSrc(projectId, imagePath) {
    if (/^(https?:|data:)/i.test(imagePath))
        return imagePath;
    if (!projectId)
        return imagePath;
    const filename = imagePath.split("/").pop() ?? imagePath;
    return api.assetUrl(projectId, filename);
}

function assetThumbnailSrc(projectId, imagePath, size = 480, version = "") {
    if (/^(https?:|data:)/i.test(imagePath))
        return imagePath;
    if (!projectId)
        return imagePath;
    const filename = imagePath.split("/").pop() ?? imagePath;
    return api.assetThumbnailUrl(projectId, filename, size, version);
}

function reportImageSrcWithVersion(projectId, imagePath, version) {
    const src = reportImageSrc(projectId, imagePath);
    if (!version || /^(data:)/i.test(src))
        return src;
    return `${src}${src.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
}

function contextImageSrc(projectId, imagePath) {
    if (/^(https?:|data:)/i.test(imagePath))
        return imagePath;
    if (!projectId)
        return imagePath;
    const filename = imagePath.split("/").pop() ?? imagePath;
    return api.contextAssetUrl(projectId, filename);
}

function pathCaptionFallback(imagePath) {
    return (imagePath.split("/").pop() ?? imagePath).replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || "Image";
}

function defaultImageCaption(file) {
    return file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || "Image";
}

const IMAGE_EDITOR_COLORS = ["#d92d20", "#f59e0b", "#12b76a", "#1570ef", "#7a3db8", "#111827", "#ffffff"];

const AUTO_RETOUCH_INSTRUCTION = "Retouche automatique : remplace l'arriere-plan par un fond neutre, propre et professionnel, sans modifier le sujet principal de l'image.";

function createAnnotationId() {
    return `ann_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function pointerPercentFromEvent(event, image) {
    if (!image)
        return null;
    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height)
        return null;
    return {
        x: clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100),
        y: clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100)
    };
}

function annotationHasSize(annotation) {
    if (annotation.type === "text")
        return Boolean(annotation.text?.trim());
    const dx = Math.abs((annotation.x2 ?? 0) - (annotation.x1 ?? 0));
    const dy = Math.abs((annotation.y2 ?? 0) - (annotation.y1 ?? 0));
    return Math.hypot(dx, dy) >= 1.1;
}

function annotationPixels(annotation, imageSize) {
    if (annotation.type === "text") {
        return {
            x: ((annotation.x ?? 0) / 100) * imageSize.width,
            y: ((annotation.y ?? 0) / 100) * imageSize.height
        };
    }
    return {
        x1: ((annotation.x1 ?? 0) / 100) * imageSize.width,
        y1: ((annotation.y1 ?? 0) / 100) * imageSize.height,
        x2: ((annotation.x2 ?? 0) / 100) * imageSize.width,
        y2: ((annotation.y2 ?? 0) / 100) * imageSize.height
    };
}

function renderAnnotationSvg(annotation, imageSize) {
    const color = annotation.color || "#d92d20";
    if (annotation.type === "text") {
        const point = annotationPixels(annotation, imageSize);
        const fontSize = Math.max(8, ((annotation.fontSize || 5) / 100) * imageSize.height);
        return (<text fill={color} fontFamily="'Plus Jakarta Sans', Arial, sans-serif" fontSize={fontSize} fontWeight="700" key={annotation.id} paintOrder="stroke fill" stroke="rgba(255,255,255,0.9)" strokeLinejoin="round" strokeWidth={Math.max(2, fontSize * 0.12)} x={point.x} y={point.y}>
        {annotation.text}
      </text>);
    }
    const points = annotationPixels(annotation, imageSize);
    const x = Math.min(points.x1, points.x2);
    const y = Math.min(points.y1, points.y2);
    const width = Math.abs(points.x2 - points.x1);
    const height = Math.abs(points.y2 - points.y1);
    const strokeWidth = Math.max(1, ((annotation.strokeWidth || 0.7) / 100) * imageSize.width);
    if (annotation.type === "rect") {
        return <rect fill="none" height={height} key={annotation.id} rx={Math.min(strokeWidth * 1.8, 12)} stroke={color} strokeWidth={strokeWidth} width={width} x={x} y={y}/>;
    }
    if (annotation.type === "ellipse") {
        return <ellipse cx={x + width / 2} cy={y + height / 2} fill="none" key={annotation.id} rx={width / 2} ry={height / 2} stroke={color} strokeWidth={strokeWidth}/>;
    }
    if (annotation.type === "arrow") {
        const headLength = Math.max(10, strokeWidth * 4.6);
        return (<g key={annotation.id}>
        <line stroke={color} strokeLinecap="round" strokeWidth={strokeWidth} x1={points.x1} x2={points.x2} y1={points.y1} y2={points.y2}/>
        <polygon fill={color} points={arrowHeadPolygon(points.x1, points.y1, points.x2, points.y2, headLength)}/>
      </g>);
    }
    return null;
}

function arrowHeadPolygon(x1, y1, x2, y2, headLength) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    if (!Number.isFinite(angle))
        return `${x2},${y2}`;
    const wing = Math.PI / 7;
    const left = {
        x: x2 - Math.cos(angle - wing) * headLength,
        y: y2 - Math.sin(angle - wing) * headLength
    };
    const right = {
        x: x2 - Math.cos(angle + wing) * headLength,
        y: y2 - Math.sin(angle + wing) * headLength
    };
    return `${x2},${y2} ${left.x},${left.y} ${right.x},${right.y}`;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function parseAspectRatio(aspect) {
    if (aspect === "1:1")
        return 1;
    if (aspect === "4:3")
        return 4 / 3;
    if (aspect === "16:9")
        return 16 / 9;
    return null;
}

function aspectHeightPercent(widthPercent, aspect, imageSize) {
    const ratio = parseAspectRatio(aspect);
    if (!ratio || !imageSize)
        return widthPercent;
    return widthPercent * (imageSize.width / imageSize.height) / ratio;
}

function aspectWidthPercent(heightPercent, aspect, imageSize) {
    const ratio = parseAspectRatio(aspect);
    if (!ratio || !imageSize)
        return heightPercent;
    return heightPercent * ratio * (imageSize.height / imageSize.width);
}

function fitCropToAspect(crop, aspect, imageSize) {
    if (!parseAspectRatio(aspect) || !imageSize)
        return crop;
    const centerX = crop.x + crop.width / 2;
    const centerY = crop.y + crop.height / 2;
    let width = crop.width;
    let height = aspectHeightPercent(width, aspect, imageSize);
    if (height > 92) {
        height = 92;
        width = aspectWidthPercent(height, aspect, imageSize);
    }
    width = clamp(width, 8, 96);
    height = clamp(height, 8, 96);
    return {
        x: clamp(centerX - width / 2, 0, 100 - width),
        y: clamp(centerY - height / 2, 0, 100 - height),
        width,
        height
    };
}

function applyCropDrag(start, mode, dx, dy, aspect, imageSize) {
    const minSize = 8;
    if (mode === "move") {
        return {
            ...start,
            x: clamp(start.x + dx, 0, 100 - start.width),
            y: clamp(start.y + dy, 0, 100 - start.height)
        };
    }
    const right = start.x + start.width;
    const bottom = start.y + start.height;
    let x = start.x;
    let y = start.y;
    let width = start.width;
    let height = start.height;
    if (mode === "nw" || mode === "sw") {
        x = clamp(start.x + dx, 0, right - minSize);
        width = right - x;
    }
    else {
        width = clamp(start.width + dx, minSize, 100 - start.x);
    }
    if (mode === "nw" || mode === "ne") {
        y = clamp(start.y + dy, 0, bottom - minSize);
        height = bottom - y;
    }
    else {
        height = clamp(start.height + dy, minSize, 100 - start.y);
    }
    if (parseAspectRatio(aspect) && imageSize) {
        height = aspectHeightPercent(width, aspect, imageSize);
        if (height > 100) {
            height = 100;
            width = aspectWidthPercent(height, aspect, imageSize);
        }
        if (mode === "nw" || mode === "ne")
            y = bottom - height;
        if (mode === "nw" || mode === "sw")
            x = right - width;
    }
    width = clamp(width, minSize, 100);
    height = clamp(height, minSize, 100);
    x = clamp(x, 0, 100 - width);
    y = clamp(y, 0, 100 - height);
    return { x, y, width, height };
}

function normalizeImageRotation(value) {
    return ((Number(value) || 0) % 360 + 360) % 360;
}

function rotatePercentPoint(x, y, delta) {
    let point = { x, y };
    const turns = Math.round(normalizeImageRotation(delta) / 90) % 4;
    for (let turn = 0; turn < turns; turn += 1) {
        point = { x: 100 - point.y, y: point.x };
    }
    return point;
}

function rotateCropPercent(crop, delta) {
    const corners = [
        rotatePercentPoint(crop.x, crop.y, delta),
        rotatePercentPoint(crop.x + crop.width, crop.y, delta),
        rotatePercentPoint(crop.x, crop.y + crop.height, delta),
        rotatePercentPoint(crop.x + crop.width, crop.y + crop.height, delta)
    ];
    const xValues = corners.map((point) => point.x);
    const yValues = corners.map((point) => point.y);
    const x = Math.min(...xValues);
    const y = Math.min(...yValues);
    return {
        x,
        y,
        width: Math.max(...xValues) - x,
        height: Math.max(...yValues) - y
    };
}

function rotateAnnotationPercent(annotation, delta) {
    if (annotation.type === "text") {
        const point = rotatePercentPoint(annotation.x, annotation.y, delta);
        return { ...annotation, x: point.x, y: point.y };
    }
    const start = rotatePercentPoint(annotation.x1, annotation.y1, delta);
    const end = rotatePercentPoint(annotation.x2, annotation.y2, delta);
    return { ...annotation, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
}

async function createRotatedImageObjectUrl(imageSrc, rotation) {
    const normalizedRotation = normalizeImageRotation(rotation);
    if (!normalizedRotation)
        return "";
    const image = await loadCanvasImage(imageSrc);
    const swapsDimensions = normalizedRotation === 90 || normalizedRotation === 270;
    const canvas = document.createElement("canvas");
    canvas.width = swapsDimensions ? image.naturalHeight : image.naturalWidth;
    canvas.height = swapsDimensions ? image.naturalWidth : image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context)
        throw new Error("Canvas indisponible.");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((normalizedRotation * Math.PI) / 180);
    context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => {
            if (value)
                resolve(value);
            else
                reject(new Error("Rotation de l'image impossible."));
        }, "image/png");
    });
    return URL.createObjectURL(blob);
}

async function renderEditedImageFile(imageSrc, imagePath, crop, imageSize, outputWidth, annotations = []) {
    const image = await loadCanvasImage(imageSrc);
    const sourceX = Math.round((crop.x / 100) * imageSize.width);
    const sourceY = Math.round((crop.y / 100) * imageSize.height);
    const sourceWidth = Math.max(1, Math.round((crop.width / 100) * imageSize.width));
    const sourceHeight = Math.max(1, Math.round((crop.height / 100) * imageSize.height));
    const targetWidth = Math.max(120, Math.round(outputWidth));
    const targetHeight = Math.max(1, Math.round(targetWidth * (sourceHeight / sourceWidth)));
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");
    if (!context)
        throw new Error("Canvas indisponible.");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);
    drawCanvasAnnotations(context, annotations, {
        imageSize,
        sourceX,
        sourceY,
        scaleX: targetWidth / sourceWidth,
        scaleY: targetHeight / sourceHeight,
        targetWidth,
        targetHeight
    });
    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => {
            if (value)
                resolve(value);
            else
                reject(new Error("Export image impossible."));
        }, "image/png");
    });
    const stem = (imagePath.split("/").pop() ?? "image").replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "_");
    return new File([blob], `${stem || "image"}_editee.png`, { type: "image/png" });
}

function drawCanvasAnnotations(context, annotations, transform) {
    annotations.forEach((annotation) => {
        if (!annotationHasSize(annotation))
            return;
        const color = annotation.color || "#d92d20";
        if (annotation.type === "text") {
            const point = annotationCanvasPoint(annotation.x, annotation.y, transform);
            const fontSize = Math.max(8, ((annotation.fontSize || 5) / 100) * transform.targetHeight);
            context.save();
            context.font = `700 ${fontSize}px "Plus Jakarta Sans", Arial, sans-serif`;
            context.textBaseline = "top";
            context.lineJoin = "round";
            context.strokeStyle = "rgba(255, 255, 255, 0.9)";
            context.lineWidth = Math.max(2, fontSize * 0.14);
            context.strokeText(annotation.text || "", point.x, point.y);
            context.fillStyle = color;
            context.fillText(annotation.text || "", point.x, point.y);
            context.restore();
            return;
        }
        const start = annotationCanvasPoint(annotation.x1, annotation.y1, transform);
        const end = annotationCanvasPoint(annotation.x2, annotation.y2, transform);
        const strokeWidth = Math.max(1, ((annotation.strokeWidth || 0.7) / 100) * transform.targetWidth);
        context.save();
        context.strokeStyle = color;
        context.fillStyle = color;
        context.lineCap = "round";
        context.lineJoin = "round";
        context.lineWidth = strokeWidth;
        if (annotation.type === "rect") {
            const x = Math.min(start.x, end.x);
            const y = Math.min(start.y, end.y);
            context.strokeRect(x, y, Math.abs(end.x - start.x), Math.abs(end.y - start.y));
        }
        else if (annotation.type === "ellipse") {
            context.beginPath();
            context.ellipse((start.x + end.x) / 2, (start.y + end.y) / 2, Math.abs(end.x - start.x) / 2, Math.abs(end.y - start.y) / 2, 0, 0, Math.PI * 2);
            context.stroke();
        }
        else if (annotation.type === "arrow") {
            context.beginPath();
            context.moveTo(start.x, start.y);
            context.lineTo(end.x, end.y);
            context.stroke();
            drawCanvasArrowHead(context, start.x, start.y, end.x, end.y, Math.max(10, strokeWidth * 4.6));
        }
        context.restore();
    });
}

function annotationCanvasPoint(xPercent = 0, yPercent = 0, transform) {
    const sourceX = (xPercent / 100) * transform.imageSize.width;
    const sourceY = (yPercent / 100) * transform.imageSize.height;
    return {
        x: (sourceX - transform.sourceX) * transform.scaleX,
        y: (sourceY - transform.sourceY) * transform.scaleY
    };
}

function drawCanvasArrowHead(context, x1, y1, x2, y2, headLength) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    if (!Number.isFinite(angle))
        return;
    const wing = Math.PI / 7;
    context.beginPath();
    context.moveTo(x2, y2);
    context.lineTo(x2 - Math.cos(angle - wing) * headLength, y2 - Math.sin(angle - wing) * headLength);
    context.lineTo(x2 - Math.cos(angle + wing) * headLength, y2 - Math.sin(angle + wing) * headLength);
    context.closePath();
    context.fill();
}

function loadCanvasImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Chargement de l'image impossible."));
        image.src = src;
    });
}

function smartDiffRows(before, after) {
    const beforeValue = (before ?? "").trim();
    const afterValue = (after ?? "").trim();
    if (!beforeValue && !afterValue)
        return [];
    if (beforeValue === afterValue)
        return [];
    if (!beforeValue)
        return splitDisplayLines(afterValue).map((text) => ({ type: "added", text }));
    if (!afterValue)
        return splitDisplayLines(beforeValue).map((text) => ({ type: "removed", text }));
    const beforeLines = splitDisplayLines(beforeValue);
    const afterLines = splitDisplayLines(afterValue);
    if (beforeLines.length === 1 && afterLines.length === 1) {
        return [{ type: "inline", segments: contextualInlineSegments(beforeLines[0], afterLines[0]) }];
    }
    if (beforeLines.length * afterLines.length > 12000) {
        return [{ type: "inline", segments: contextualInlineSegments(beforeValue, afterValue) }];
    }
    const operations = lineDiffOperations(beforeLines, afterLines);
    const rows = [];
    operations.forEach((operation, operationIndex) => {
        if (operation.type === "equal") {
            pushContextRows(rows, beforeLines.slice(operation.beforeStart, operation.beforeEnd), operationIndex > 0, operationIndex < operations.length - 1);
            return;
        }
        if (operation.type === "delete") {
            beforeLines.slice(operation.beforeStart, operation.beforeEnd).forEach((text) => rows.push({ type: "removed", text }));
            return;
        }
        if (operation.type === "insert") {
            afterLines.slice(operation.afterStart, operation.afterEnd).forEach((text) => rows.push({ type: "added", text }));
            return;
        }
        const removed = beforeLines.slice(operation.beforeStart, operation.beforeEnd);
        const added = afterLines.slice(operation.afterStart, operation.afterEnd);
        const pairedCount = Math.min(removed.length, added.length);
        for (let index = 0; index < pairedCount; index += 1) {
            if (lineSimilarity(removed[index], added[index]) >= 0.28) {
                rows.push({ type: "inline", segments: contextualInlineSegments(removed[index], added[index]) });
            }
            else {
                rows.push({ type: "removed", text: removed[index] });
                rows.push({ type: "added", text: added[index] });
            }
        }
        removed.slice(pairedCount).forEach((text) => rows.push({ type: "removed", text }));
        added.slice(pairedCount).forEach((text) => rows.push({ type: "added", text }));
    });
    return rows.filter((row, index, all) => row.type !== "ellipsis" || all[index - 1]?.type !== "ellipsis");
}

function splitDisplayLines(value) {
    return value.replace(/\r\n/g, "\n").split("\n").filter((line) => line.trim());
}

function pushContextRows(rows, lines, hasPreviousChange, hasNextChange) {
    if (!hasPreviousChange && !hasNextChange)
        return;
    const context = [];
    if (lines.length <= 2) {
        context.push(...lines);
    }
    else if (!hasPreviousChange) {
        context.push(lines[lines.length - 1]);
    }
    else if (!hasNextChange) {
        context.push(lines[0]);
    }
    else {
        context.push(lines[0], "__ELLIPSIS__", lines[lines.length - 1]);
    }
    context.forEach((text) => {
        if (text === "__ELLIPSIS__")
            rows.push({ type: "ellipsis" });
        else
            rows.push({ type: "context", text });
    });
}

function lineDiffOperations(beforeLines, afterLines) {
    const rows = beforeLines.length + 1;
    const cols = afterLines.length + 1;
    const dp = Array.from({ length: rows }, () => Array(cols).fill(0));
    for (let row = beforeLines.length - 1; row >= 0; row -= 1) {
        for (let col = afterLines.length - 1; col >= 0; col -= 1) {
            dp[row][col] =
                beforeLines[row] === afterLines[col]
                    ? dp[row + 1][col + 1] + 1
                    : Math.max(dp[row + 1][col], dp[row][col + 1]);
        }
    }
    const raw = [];
    let row = 0;
    let col = 0;
    while (row < beforeLines.length || col < afterLines.length) {
        if (row < beforeLines.length && col < afterLines.length && beforeLines[row] === afterLines[col]) {
            raw.push({ type: "equal", beforeIndex: row, afterIndex: col });
            row += 1;
            col += 1;
        }
        else if (col < afterLines.length && (row >= beforeLines.length || dp[row][col + 1] >= dp[row + 1][col])) {
            raw.push({ type: "insert", beforeIndex: row, afterIndex: col });
            col += 1;
        }
        else {
            raw.push({ type: "delete", beforeIndex: row, afterIndex: col });
            row += 1;
        }
    }
    return coalesceLineOperations(raw, beforeLines.length, afterLines.length);
}

function coalesceLineOperations(raw, beforeLength, afterLength) {
    const operations = [];
    let index = 0;
    while (index < raw.length) {
        const start = raw[index];
        const beforeStart = start.beforeIndex;
        const afterStart = start.afterIndex;
        let beforeEnd = beforeStart + (start.type === "insert" ? 0 : 1);
        let afterEnd = afterStart + (start.type === "delete" ? 0 : 1);
        index += 1;
        while (index < raw.length && raw[index].type === start.type) {
            beforeEnd = raw[index].beforeIndex + (raw[index].type === "insert" ? 0 : 1);
            afterEnd = raw[index].afterIndex + (raw[index].type === "delete" ? 0 : 1);
            index += 1;
        }
        operations.push({ type: start.type, beforeStart, beforeEnd, afterStart, afterEnd });
    }
    const merged = [];
    for (let opIndex = 0; opIndex < operations.length; opIndex += 1) {
        const current = operations[opIndex];
        const next = operations[opIndex + 1];
        if (current.type === "delete" && next?.type === "insert") {
            merged.push({
                type: "replace",
                beforeStart: current.beforeStart,
                beforeEnd: current.beforeEnd,
                afterStart: next.afterStart,
                afterEnd: next.afterEnd,
            });
            opIndex += 1;
        }
        else if (current.type === "insert" && next?.type === "delete") {
            merged.push({
                type: "replace",
                beforeStart: next.beforeStart,
                beforeEnd: next.beforeEnd,
                afterStart: current.afterStart,
                afterEnd: current.afterEnd,
            });
            opIndex += 1;
        }
        else {
            merged.push(current);
        }
    }
    if (!merged.length && (beforeLength || afterLength)) {
        return [{ type: "replace", beforeStart: 0, beforeEnd: beforeLength, afterStart: 0, afterEnd: afterLength }];
    }
    return merged;
}

function contextualInlineSegments(before, after) {
    return compactUnchangedSegments(inlineDiffSegments(before, after));
}

function compactUnchangedSegments(segments) {
    return segments
        .map((segment, index, all) => {
        if (segment.type !== "same")
            return segment;
        const hasPreviousChange = all.slice(0, index).some((item) => item.type !== "same");
        const hasNextChange = all.slice(index + 1).some((item) => item.type !== "same");
        const text = trimUnchangedContext(segment.text, hasPreviousChange, hasNextChange);
        return text ? { ...segment, text } : null;
    })
        .filter((segment) => Boolean(segment));
}

function trimUnchangedContext(text, hasPreviousChange, hasNextChange) {
    if (!hasPreviousChange && !hasNextChange)
        return "";
    if (!text.trim())
        return " ";
    if (text.length <= 90)
        return text;
    if (hasPreviousChange && hasNextChange)
        return " ... ";
    if (hasPreviousChange)
        return `${text.slice(0, 70)}...`;
    return `...${text.slice(-70)}`;
}

function lineSimilarity(before, after) {
    const beforeWords = new Set((before.toLowerCase().match(/[\p{L}\p{N}_-]+/gu) ?? []).filter((word) => word.length > 2));
    const afterWords = new Set((after.toLowerCase().match(/[\p{L}\p{N}_-]+/gu) ?? []).filter((word) => word.length > 2));
    if (!beforeWords.size || !afterWords.size)
        return 0;
    let shared = 0;
    beforeWords.forEach((word) => {
        if (afterWords.has(word))
            shared += 1;
    });
    return shared / Math.max(beforeWords.size, afterWords.size);
}

function diffSummary(rows) {
    let added = 0;
    let removed = 0;
    rows.forEach((row) => {
        if (row.type === "added")
            added += countMeaningfulWords(row.text);
        if (row.type === "removed")
            removed += countMeaningfulWords(row.text);
        if (row.type === "inline") {
            row.segments.forEach((segment) => {
                if (segment.type === "added")
                    added += countMeaningfulWords(segment.text);
                if (segment.type === "removed")
                    removed += countMeaningfulWords(segment.text);
            });
        }
    });
    if (!added && !removed)
        return "Aucune difference textuelle nette";
    const parts = [];
    if (added)
        parts.push(`${added} ajout${added > 1 ? "s" : ""}`);
    if (removed)
        parts.push(`${removed} retrait${removed > 1 ? "s" : ""}`);
    return parts.join(", ");
}

function countMeaningfulWords(text) {
    return text.match(/[\p{L}\p{N}_-]+/gu)?.length ?? 0;
}

function inlineDiffSegments(before, after) {
    const beforeTokens = tokenizeDiffText(before);
    const afterTokens = tokenizeDiffText(after);
    if (!beforeTokens.length && !afterTokens.length)
        return [];
    if (beforeTokens.length * afterTokens.length > 120000)
        return prefixSuffixDiff(before, after);
    const rows = beforeTokens.length + 1;
    const cols = afterTokens.length + 1;
    const dp = Array.from({ length: rows }, () => Array(cols).fill(0));
    for (let row = beforeTokens.length - 1; row >= 0; row -= 1) {
        for (let col = afterTokens.length - 1; col >= 0; col -= 1) {
            dp[row][col] =
                beforeTokens[row] === afterTokens[col]
                    ? dp[row + 1][col + 1] + 1
                    : Math.max(dp[row + 1][col], dp[row][col + 1]);
        }
    }
    const segments = [];
    let row = 0;
    let col = 0;
    while (row < beforeTokens.length || col < afterTokens.length) {
        if (row < beforeTokens.length && col < afterTokens.length && beforeTokens[row] === afterTokens[col]) {
            pushDiffSegment(segments, beforeTokens[row], "same");
            row += 1;
            col += 1;
        }
        else if (col < afterTokens.length && (row >= beforeTokens.length || dp[row][col + 1] >= dp[row + 1][col])) {
            pushDiffSegment(segments, afterTokens[col], "added");
            col += 1;
        }
        else if (row < beforeTokens.length) {
            pushDiffSegment(segments, beforeTokens[row], "removed");
            row += 1;
        }
    }
    return compactWhitespaceDiff(segments);
}

function tokenizeDiffText(value) {
    return value.match(/\s+|[^\s]+/g) ?? [];
}

function pushDiffSegment(segments, text, type) {
    const previous = segments[segments.length - 1];
    if (previous?.type === type)
        previous.text += text;
    else
        segments.push({ text, type });
}

function compactWhitespaceDiff(segments) {
    return segments.filter((segment, index, all) => {
        if (segment.text.trim())
            return true;
        return all[index - 1]?.type === "same" || all[index + 1]?.type === "same";
    });
}

function prefixSuffixDiff(before, after) {
    let start = 0;
    while (start < before.length && start < after.length && before[start] === after[start])
        start += 1;
    let beforeEnd = before.length;
    let afterEnd = after.length;
    while (beforeEnd > start && afterEnd > start && before[beforeEnd - 1] === after[afterEnd - 1]) {
        beforeEnd -= 1;
        afterEnd -= 1;
    }
    return [
        before.slice(0, start) ? { text: before.slice(0, start), type: "same" } : null,
        before.slice(start, beforeEnd) ? { text: before.slice(start, beforeEnd), type: "removed" } : null,
        after.slice(start, afterEnd) ? { text: after.slice(start, afterEnd), type: "added" } : null,
        after.slice(afterEnd) ? { text: after.slice(afterEnd), type: "same" } : null
    ].filter((segment) => Boolean(segment));
}

function commentsForBlock(block, comments, blockIndex) {
    return comments.filter((comment) => {
        if (comment.status === "dismissed" || comment.status === "applied")
            return false;
        if (isStaleFailedChartComment(comment, block))
            return false;
        if (comment.block_index === blockIndex)
            return true;
        if (blockIndex === 0 &&
            comment.block_index == null &&
            (comment.target_type === "section" || comment.edit_suggestion?.status === "pending" || comment.edit_suggestion?.status === "ready")) {
            return true;
        }
        if (block.type === "image") {
            return (comment.target_type === "image" || comment.target_type === "image_edit") && comment.image_path === block.path;
        }
        if (block.type === "chart") {
            const sourcePath = chartSourcePath(block.spec);
            if (comment.target_type === "chart") {
                const target = String(comment.selected_text ?? "");
                const rawStart = String(block.raw ?? "").slice(0, 300);
                return Boolean(target && ((rawStart && target.includes(rawStart)) || target.includes(chartTitle(block.spec))));
            }
            return sourcePath && (comment.target_type === "image" || comment.target_type === "image_edit") && comment.image_path === sourcePath;
        }
        if ("text" in block && comment.selected_text)
            return block.text.includes(comment.selected_text);
        return false;
    });
}

function isObsoleteSuggestionForSection(comment, sectionMarkdown) {
    const suggestion = comment?.edit_suggestion;
    if (!suggestion)
        return false;
    if (isSectionChangedSuggestionError(suggestion.error))
        return true;
    if (!suggestion.original_markdown)
        return false;
    return normalizeSuggestionMarkdown(sectionMarkdown) !== normalizeSuggestionMarkdown(suggestion.original_markdown);
}

function isSectionChangedSuggestionError(error) {
    const text = normalizeSearchText(error);
    return text.includes("section a change") && text.includes("suggestion");
}

function normalizeSuggestionMarkdown(value) {
    return String(value ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

function normalizeSearchText(value) {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}

function isStaleFailedChartComment(comment, block) {
    if (block?.type !== "chart" || comment.target_type !== "chart" || comment.edit_suggestion?.status !== "failed")
        return false;
    const targetFingerprint = chartCommentSpecFingerprint(comment.selected_text);
    const blockFingerprint = chartSpecFingerprint(block.spec);
    if (targetFingerprint && blockFingerprint)
        return targetFingerprint !== blockFingerprint;
    const target = normalizeChartCommentText(comment.selected_text);
    const raw = normalizeChartCommentText(block.raw || chartBlockToMarkdown(block.spec));
    return Boolean(target && raw && target !== raw);
}

function chartCommentSpecFingerprint(markdown) {
    const block = chartPreviewBlockFromMarkdown(markdown);
    return block ? chartSpecFingerprint(block.spec) : "";
}

function chartSpecFingerprint(spec) {
    try {
        return JSON.stringify(cleanChartSpecForSave(chartSpecForEditor(spec)));
    }
    catch {
        return "";
    }
}

function normalizeChartCommentText(value) {
    return String(value ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

function renderKatexInline(text) {
    const parts = text.split(/(\\\([\s\S]*?\\\))/g);
    if (parts.length === 1)
        return text;
    return (<>
      {parts.map((part, i) => {
            const match = part.match(/^\\\(([\s\S]*)\\\)$/);
            if (match) {
                try {
                    const html = katex.renderToString(match[1], { throwOnError: false, strict: false });
                    return <span key={i} className="math-inline" dangerouslySetInnerHTML={{ __html: html }}/>;
                }
                catch {
                    return <code key={i}>{match[1]}</code>;
                }
            }
            return part || null;
        })}
    </>);
}

function normalizeInlineLinks(text) {
    return String(text ?? "")
        .replace(/\]\s+\((https?:\/\/[^)\s]+)\)/g, "]($1)")
        .replace(/(^|[^A-Za-z0-9._\-\x5B!])((?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,})\]\((https?:\/\/[^)\s]+)\)/gm, "$1[$2]($3)");
}

function splitTrailingLinkPunctuation(url) {
    const match = String(url || "").match(/^([\s\S]*?)([.,;:!?]+)?$/);
    return {
        href: match?.[1] || url,
        trailing: match?.[2] || ""
    };
}

function renderFormattedInlineMarkdown(text, renderMath = true) {
    const renderContent = (value) => renderMath ? renderKatexInline(value) : value;
    const parts = text.split(/(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g);
    if (parts.length === 1)
        return renderContent(text);
    return (<>
      {parts.map((part, i) => {
            const boldItalic = part.match(/^\*\*\*([\s\S]+)\*\*\*$/);
            if (boldItalic)
                return <strong key={i}><em>{renderContent(boldItalic[1])}</em></strong>;
            const bold = part.match(/^\*\*([\s\S]+)\*\*$/);
            if (bold)
                return <strong key={i}>{renderContent(bold[1])}</strong>;
            const italic = part.match(/^\*([\s\S]+)\*$/);
            if (italic)
                return <em key={i}>{renderContent(italic[1])}</em>;
            return renderContent(part) || null;
        })}
    </>);
}

function renderInlineMarkdown(text, renderMath = true) {
    const normalized = normalizeInlineLinks(text);
    const linkPattern = /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s<)]+)/gi;
    const rendered = [];
    let lastIndex = 0;
    let match;
    const pushText = (value) => {
        if (!value)
            return;
        rendered.push(<span key={`text-${rendered.length}`}>{renderFormattedInlineMarkdown(value, renderMath)}</span>);
    };
    while ((match = linkPattern.exec(normalized)) !== null) {
        pushText(normalized.slice(lastIndex, match.index));
        const label = match[1];
        const explicitHref = match[2];
        const bareHref = match[3];
        if (explicitHref) {
            rendered.push(<a className="markdown-link" href={explicitHref} key={`link-${rendered.length}`} rel="noreferrer" target="_blank">
          {renderFormattedInlineMarkdown(label, renderMath)}
        </a>);
        }
        else if (bareHref) {
            const { href, trailing } = splitTrailingLinkPunctuation(bareHref);
            rendered.push(<a className="markdown-link" href={href} key={`link-${rendered.length}`} rel="noreferrer" target="_blank">
          {href}
        </a>);
            pushText(trailing);
        }
        lastIndex = match.index + match[0].length;
    }
    pushText(normalized.slice(lastIndex));
    if (!rendered.length)
        return renderFormattedInlineMarkdown(normalized, renderMath);
    return <>{rendered}</>;
}

function renderCommentedText(text, comments) {
    const target = comments
        .map((comment) => comment.selected_text)
        .filter((value) => value && text.includes(value))
        .sort((a, b) => b.length - a.length)[0];
    if (!target)
        return renderInlineMarkdown(text);
    const index = text.indexOf(target);
    return (<>
      {renderInlineMarkdown(text.slice(0, index))}
      <mark className="comment-highlight">{target}</mark>
      {renderInlineMarkdown(text.slice(index + target.length))}
    </>);
}

function MermaidPreview({ code }) {
    const graph = parseMermaidFlowchart(code);
    if (!graph.nodes.length) {
        return <pre className="mermaid-fallback">{code}</pre>;
    }
    const nodeMap = new Map(graph.nodes.map((node) => [node.id, node]));
    const maxX = Math.max(...graph.nodes.map((node) => node.x + node.width / 2), 320);
    const maxY = Math.max(...graph.nodes.map((node) => node.y + node.height / 2), 180);
    const minX = Math.min(...graph.nodes.map((node) => node.x - node.width / 2), 0);
    const minY = Math.min(...graph.nodes.map((node) => node.y - node.height / 2), 0);
    const pad = 34;
    return (<figure className="mermaid-preview">
      <svg role="img" aria-label="Diagramme de flux" viewBox={`${minX - pad} ${minY - pad} ${maxX - minX + pad * 2} ${maxY - minY + pad * 2}`}>
        <defs>
          <marker id="mermaid-arrow" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
            <path d="M0,0 L8,4 L0,8 z"/>
          </marker>
        </defs>
        {graph.edges.map((edge, index) => {
            const source = nodeMap.get(edge.source);
            const target = nodeMap.get(edge.target);
            if (!source || !target)
                return null;
            const startX = source.x + source.width / 2;
            const endX = target.x - target.width / 2;
            return (<line key={`${edge.source}-${edge.target}-${index}`} className="mermaid-edge" markerEnd="url(#mermaid-arrow)" x1={startX} x2={endX} y1={source.y} y2={target.y}/>);
        })}
        {graph.nodes.map((node) => (<g key={node.id} className="mermaid-node">
            <rect height={node.height} rx="7" width={node.width} x={node.x - node.width / 2} y={node.y - node.height / 2}/>
            <text textAnchor="middle" x={node.x} y={node.y - ((node.lines.length - 1) * 8)}>
              {node.lines.map((line, index) => (<tspan dy={index ? 17 : 0} key={index} x={node.x}>
                  {line}
                </tspan>))}
            </text>
          </g>))}
      </svg>
      <figcaption>Diagramme de flux.</figcaption>
    </figure>);
}

export {
    AUTO_RETOUCH_INSTRUCTION,
    IMAGE_EDITOR_COLORS,
    ImageDiffColumn,
    MermaidPreview,
    annotationCanvasPoint,
    annotationHasSize,
    annotationPixels,
    applyCropDrag,
    arrowHeadPolygon,
    aspectHeightPercent,
    aspectWidthPercent,
    assetThumbnailSrc,
    chartCommentSpecFingerprint,
    chartCommentTitle,
    chartPreviewBlockFromMarkdown,
    chartSpecDiffItems,
    chartSpecFingerprint,
    clamp,
    coalesceLineOperations,
    commentsForBlock,
    compactUnchangedSegments,
    compactWhitespaceDiff,
    contextImageSrc,
    contextualInlineSegments,
    countMeaningfulWords,
    createAnnotationId,
    createRotatedImageObjectUrl,
    defaultImageCaption,
    diffSummary,
    drawCanvasAnnotations,
    drawCanvasArrowHead,
    fitCropToAspect,
    formatChartDiffValue,
    inlineDiffSegments,
    isObsoleteSuggestionForSection,
    isSectionChangedSuggestionError,
    isStaleFailedChartComment,
    lineDiffOperations,
    lineSimilarity,
    loadCanvasImage,
    markdownImageRefs,
    normalizeChartCommentText,
    normalizeImageRotation,
    normalizeInlineLinks,
    normalizeSearchText,
    normalizeSuggestionMarkdown,
    parseAspectRatio,
    pathCaptionFallback,
    pointerPercentFromEvent,
    prefixSuffixDiff,
    pushContextRows,
    pushDiffSegment,
    renderAnnotationSvg,
    renderCommentedText,
    renderEditedImageFile,
    renderFormattedInlineMarkdown,
    renderInlineMarkdown,
    renderKatexInline,
    reportImageSrc,
    reportImageSrcWithVersion,
    rotateAnnotationPercent,
    rotateCropPercent,
    rotatePercentPoint,
    smartDiffRows,
    splitDisplayLines,
    splitTrailingLinkPunctuation,
    tablePreviewBlocksFromMarkdown,
    tokenizeDiffText,
    trimUnchangedContext
};
